// Product prices and Appstle allocations come from Shopify's locale-aware Ajax API.
export const PACK_HANDLES = ['nurox-focus-pouches','nurox-focus-pouches-5-pack','nurox-focus-pouches-10-pack'];
const SIZES = [3,5,10];
const FLAVORS = ['Blueberry','Peach','Wintergreen'];

export function plansForVariant(product, variant) {
  if (!variant) return [];
  const plans = (product.selling_plan_groups || []).filter(g=>g.app_id === 'appstle')
    .flatMap(g=>g.selling_plans || []).filter(p=>p.recurring_deliveries);
  return (variant.selling_plan_allocations || []).flatMap(allocation=>{
    const plan = plans.find(p=>String(p.id) === String(allocation.selling_plan_id));
    if (!plan || !Number.isFinite(allocation.price)) return [];
    const match = plan.name.match(/^Delivery every (\d+) weeks?$/i);
    return [{id:String(plan.id),name:plan.name,label:match?`${match[1]} weeks`:plan.name,
      cadence:plan.name, weeks:match?Number(match[1]):Infinity, price:allocation.price,
      allocation}];
  }).sort((a,b)=>a.weeks-b.weeks || a.name.localeCompare(b.name));
}
export function selectionFromVariant(products, variantId) {
  if (!variantId) return null;
  for (const product of products) {
    const packIndex = PACK_HANDLES.indexOf(product.handle);
    const variant = product.variants.find(v=>String(v.id) === String(variantId));
    if (packIndex >= 0 && variant && FLAVORS.includes(variant.title)) {
      return {cans:SIZES[packIndex],flavor:variant.title};
    }
  }
  return null;
}
export function resolveSelection(products, state) {
  const product = products.find(p=>p.handle === PACK_HANDLES[SIZES.indexOf(Number(state.cans))]);
  const variant = product?.variants.find(v=>v.title === state.flavor) || null;
  const plans = product ? plansForVariant(product,variant) : [];
  const plan = plans.find(p=>p.cadence === state.cadence) || null;
  const validMode = state.mode === 'sub' ? !!plan : state.mode === 'once' && !variant?.requires_selling_plan;
  return {product,variant,plans,plan,price:state.mode === 'sub'?plan?.price:variant?.price,
    buyable:!!variant?.available && validMode};
}
export function isPreorder(variant, inventory) {
  return !!variant?.available && !!inventory?.tracked && inventory.quantity <= 0 && inventory.policy === 'continue';
}
export function cartItem(selection, mode) {
  if (!selection.buyable) throw new Error('This selection is unavailable.');
  const item={id:String(selection.variant.id),quantity:1};
  if (mode === 'sub') item.selling_plan=selection.plan.id;
  return item;
}

export async function mountCommerce(root) {
  if (root.dataset.commerceMounted) return;
  root.dataset.commerceMounted='true';
  const form=root.querySelector('[data-nx4-form]');
  const atc=root.querySelector('[data-nx4-atc]');
  const atcText=root.querySelector('[data-nx4-atc-text]');
  const flavorsEl=root.querySelector('[data-nx4-flavors]');
  const packsEl=root.querySelector('[data-nx4-packs]');
  const subRadio=root.querySelector('[name="nx4-mode"][value="sub"]');
  const onceRadio=root.querySelector('[name="nx4-mode"][value="once"]');
  const chips=root.querySelector('.nx4__freq-chips');
  const subTitle=subRadio.closest('.nx4__mode-row').querySelector('.nx4__mode-title');
  const subPrice=root.querySelector('[data-nx4-sub-price]');
  const oncePrice=root.querySelector('[data-nx4-once-price]');
  const stock=root.querySelector('[data-nx4-oos]');
  const route=window.Shopify?.routes?.root || '/';
  const money=new Intl.NumberFormat(document.documentElement.lang || 'en-US',{
    style:'currency',currency:root.dataset.currency || window.Shopify?.currency?.active || 'USD'
  });
  const perTinMoney=new Intl.NumberFormat(document.documentElement.lang || 'en-US',{
    style:'currency',currency:money.resolvedOptions().currency,minimumFractionDigits:0,maximumFractionDigits:1
  });
  const format=value=>Number.isFinite(value)?money.format(value/100):'—';
  const abort=new AbortController();
  const on=(element,event,fn)=>element.addEventListener(event,fn,{signal:abort.signal});
  let products=[],busy=false,selection;
  const state={cans:3,flavor:'Blueberry',mode:'sub',cadence:'Delivery every 4 weeks'};
  const message=document.createElement('p');message.className='nx4__terms';message.setAttribute('role','status');
  message.setAttribute('aria-live','polite');message.hidden=true;atc.after(message);
  const field=name=>{let input=form.querySelector(`input[type="hidden"][name="${name}"]`);if(!input){input=document.createElement('input');input.type='hidden';input.name=name;form.append(input);}return input;};
  atc.disabled=true;atcText.textContent='Loading';subTitle.textContent='Subscribe';
  subPrice.textContent='';oncePrice.textContent='';if(stock)stock.hidden=true;
  packsEl.querySelectorAll('.nx4__pack-total,.nx4__pack-per').forEach(el=>el.textContent='');
  form.setAttribute('aria-busy','true');
  form.querySelectorAll('input').forEach(input=>input.disabled=true);
  // Keep the approved flavor artwork, but build purchasing state from live variants.
  const artwork={};flavorsEl.querySelectorAll('label').forEach(label=>{
    const input=label.querySelector('input');const img=label.querySelector('img');
    if(input && img)artwork[input.value]=img.getAttribute('src');
  });
  // The production shell has empty flavor labels; its approved assets are supplied as data.
  Object.assign(artwork,JSON.parse(root.dataset.flavorImages || '{}'));
  const showMessage=text=>{message.textContent=text;message.hidden=!text;};

  function syncFields() {
    field('quantity').value='1';field('quantity').disabled=false;
    const id=field('id');id.value=selection.variant?.id || '';id.disabled=!selection.variant;
    const plan=field('selling_plan');plan.value=selection.plan?.id || '';plan.disabled=state.mode!=='sub' || !selection.plan;
  }
  function render() {
    selection=resolveSelection(products,state);
    const subAvailable=selection.plans.length>0;
    if (!subAvailable && !selection.variant?.requires_selling_plan) state.mode='once';
    if (selection.variant?.requires_selling_plan) state.mode='sub';
    // Preserve cadence across products when offered; otherwise visibly select a real plan.
    if (!selection.plan && subAvailable) state.cadence=(selection.plans.find(p=>p.weeks===4)||selection.plans[0]).cadence;
    selection=resolveSelection(products,state);
    subRadio.disabled=!subAvailable;onceRadio.disabled=!!selection.variant?.requires_selling_plan;
    subRadio.checked=state.mode==='sub';onceRadio.checked=state.mode==='once';
    chips.closest('.nx4__freq').hidden=!subAvailable;
    const signature=selection.plans.map(p=>`${p.id}:${p.label}`).join('|');
    if(chips.dataset.signature!==signature){
      chips.replaceChildren();chips.dataset.signature=signature;
      for(const plan of selection.plans){
        const label=document.createElement('label');label.className='nx4__chip';
        const input=document.createElement('input');input.type='radio';input.name='nx4-freq';input.value=plan.id;
        const face=document.createElement('span');face.className='nx4__chip-face';face.textContent=plan.label;
        label.append(input,face);chips.append(label);
      }
    }
    chips.querySelectorAll('input').forEach(input=>input.checked=input.value===selection.plan?.id);
    const discount=selection.plan && selection.variant.price>0?Math.round((1-selection.plan.price/selection.variant.price)*100):0;
    subTitle.textContent=discount>0?`Subscribe, save ${discount}%`:'Subscribe';
    subPrice.textContent=format(selection.plan?.price);oncePrice.textContent=format(selection.variant?.price);
    packsEl.querySelectorAll('.nx4__pack').forEach(label=>{
      const cans=Number(label.querySelector('input').value);
      const result=resolveSelection(products,{...state,cans});
      label.querySelector('.nx4__pack-total').textContent=format(result.price);
      const per=label.querySelector('.nx4__pack-per');
      per.replaceChildren();
      if(Number.isFinite(result.price)){
        const value=document.createElement('span');value.className='nx4__pack-per-value';
        value.textContent=perTinMoney.format(result.price/cans/100);
        const unit=document.createElement('span');unit.className='nx4__pack-per-unit';unit.textContent='/TIN';
        per.append(value,unit);
      }
    });
    flavorsEl.querySelectorAll('label').forEach(label=>{
      const input=label.querySelector('input');const variant=selection.product?.variants.find(v=>v.title===input.value);
      input.checked=input.value===state.flavor;input.disabled=!variant;
      label.classList.toggle('nx4__flavor--nostock',!variant?.available);
    });
    atc.disabled=busy || !selection.buyable;
    const inventoryRows=JSON.parse(document.querySelector('[data-nurox-inventory]')?.textContent || '[]');
    const inventory=inventoryRows.find(row=>String(row.id)===String(selection.variant?.id));
    // Local Ajax data omits inventory fields; owner confirmed current stock is zero.
    const inventoryState=root.dataset.localPreview==='true'?{tracked:true,quantity:0,policy:'continue'}:inventory;
    const preorder=isPreorder(selection.variant,inventoryState);
    atcText.textContent=busy?'Adding':selection.buyable?(preorder?'Pre-order':'Add to cart'):'Sold out';
    if(stock){stock.hidden=!preorder;const note=stock.querySelector('.nx4__stock-v');if(note)note.textContent='Ships October 3';}
    root.querySelectorAll('.nx4__trust li').forEach(item=>{if(item.textContent.includes('Ships from Salt Lake'))item.hidden=preorder;});
    syncFields();
  }
  function buildControls(){
    flavorsEl.replaceChildren();packsEl.replaceChildren();
    for(const flavor of FLAVORS){
      const label=document.createElement('label');label.className='nx4__flavor';
      const input=document.createElement('input');input.type='radio';input.name='nx4-flavor';input.value=flavor;
      const face=document.createElement('span');face.className='nx4__flavor-face';
      if(artwork[flavor]){const img=document.createElement('img');img.className='nx4__flavor-img';img.src=artwork[flavor];img.alt='';img.width=240;img.height=240;face.append(img);}
      const name=document.createElement('span');name.className='nx4__flavor-name';name.textContent=flavor;face.append(name);
      label.append(input,face);flavorsEl.append(label);
    }
    for(const cans of SIZES){
      const label=document.createElement('label');label.className='nx4__pack';
      const input=document.createElement('input');input.type='radio';input.name='nx4-pack';input.value=String(cans);input.checked=cans===state.cans;
      const face=document.createElement('span');face.className='nx4__pack-face';
      const title=document.createElement('span');title.className='nx4__pack-cans';
      const number=document.createElement('span');number.className='nx4__pack-n';number.textContent=String(cans);
      const unit=document.createElement('span');unit.className='nx4__pack-u';unit.textContent='pack';title.append(number,' ',unit);
      const total=document.createElement('span');total.className='nx4__pack-total';
      const per=document.createElement('span');per.className='nx4__pack-per';face.append(title,total,per);label.append(input,face);packsEl.append(label);
    }
  }
  on(form,'change',event=>{
    const input=event.target;if(!input.checked)return;
    if(input.name==='nx4-flavor')state.flavor=input.value;
    else if(input.name==='nx4-pack')state.cans=Number(input.value);
    else if(input.name==='nx4-mode')state.mode=input.value;
    else if(input.name==='nx4-freq'){
      const plan=selection.plans.find(p=>p.id===input.value);if(!plan)return;
      state.cadence=plan.cadence;state.mode='sub';
    }else return;
    showMessage('');render();
  });
  on(form,'submit',async event=>{
    event.preventDefault();if(busy || !selection?.buyable)return;
    const item=cartItem(selection,state.mode);
    if(root.dataset.localPreview==='true'){showMessage('Cart testing is available in the Shopify preview.');return;}
    busy=true;render();showMessage('');
    try{
      const data=new FormData();Object.entries(item).forEach(([key,value])=>data.set(key,String(value)));
      const sections=[...document.querySelectorAll('cart-items-component[data-section-id]')].map(c=>c.dataset.sectionId);
      if(sections.length)data.set('sections',[...new Set(sections)].slice(0,5).join(','));
      const response=await fetch(`${route}cart/add?appstle=true`,{method:'POST',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},body:data,signal:abort.signal});
      const added=await response.json();if(!response.ok || added.status>=400)throw new Error(added.description || 'Unable to add this selection.');
      document.dispatchEvent(new CustomEvent('cart:update',{bubbles:true,detail:{source:'nx4-pdp',data:{sections:added.sections || {}}}}));
      showMessage('Added to cart');
    }catch(error){if(error.name!=='AbortError')showMessage(error.message || 'Unable to add this selection.');}
    finally{busy=false;render();}
  });
  on(document,'shopify:section:unload',event=>{if(root.closest('.shopify-section')?.id===`shopify-section-${event.detail?.sectionId}`)abort.abort();});
  try{
    products=await Promise.all(PACK_HANDLES.map(async handle=>{
      const response=await fetch(`${route}products/${handle}.js`,{headers:{Accept:'application/json'},signal:abort.signal});
      if(!response.ok)throw new Error('Unable to load purchasing options. Please refresh.');
      const product=await response.json();if(product.handle!==handle || !Array.isArray(product.variants))throw new Error('Unable to load purchasing options. Please refresh.');return product;
    }));
    const linkedSelection=selectionFromVariant(products,new URLSearchParams(window.location.search).get('variant'));
    if(linkedSelection)Object.assign(state,linkedSelection);
    buildControls();render();form.removeAttribute('aria-busy');
  }catch(error){form.removeAttribute('aria-busy');atc.disabled=true;atcText.textContent='Unavailable';showMessage(error.message);}
}
if(typeof document!=='undefined'){
  const mount=()=>document.querySelectorAll('[data-nx4]').forEach(root=>mountCommerce(root));
  mount();document.addEventListener('shopify:section:load',mount);
}
